// src/components/SynergyIntegration.jsx
import React, { useState } from 'react';
import axios from 'axios';
import { 
  Button, 
  message as antMessage, 
  Table, 
  Card, 
  Space, 
  Typography, 
  Spin,
  Alert,
  Tag,
  Tooltip,
  DatePicker,
  Row,
  Col,
  Statistic
} from 'antd';
import { 
  DownloadOutlined, 
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const SynergyIntegration = () => {
  // Calcular fechas por defecto: hoy y hace 7 días
  const today = dayjs();
  const sevenDaysAgo = dayjs().subtract(7, 'day');
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [reservations, setReservations] = useState([]);
  const [dateRange, setDateRange] = useState([sevenDaysAgo, today]);
  const [totalReservations, setTotalReservations] = useState(0);

  const testConnection = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/synergy/test-connection`);
      setMessage(response.data.message);
      antMessage.success('Conexión exitosa');
    } catch (error) {
      const errorMsg = 'Error: ' + error.response?.data?.error || error.message;
      setMessage(errorMsg);
      antMessage.error(errorMsg);
    }
    setLoading(false);
  };

  const fetchReservations = async () => {
    if (!dateRange || dateRange.length !== 2) {
      antMessage.warning('Por favor selecciona un rango de fechas válido');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [start, end] = dateRange;
      
      // Formatear fechas en el formato requerido por el backend
      const startDate = start.format('YYYY-MM-DDTHH:mm:ssZ');
      const endDate = end.format('YYYY-MM-DDTHH:mm:ssZ');
      
      const response = await axios.post(
        `${BASE_URL}/api/integraciones/get-books`,
        {
          startDate: startDate,
          endDate: endDate,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      console.log(" Reservas => ",response.data.data);
      
      
      setReservations(response.data.data);
      setTotalReservations(response.data.data.length);
      
      const successMsg = `Encontradas ${response.data.data.length} reservas`;
      setMessage(successMsg);
      antMessage.success(successMsg);
    } catch (error) {
      const errorMsg = 'Error: ' + error.response?.data?.error || error.message;
      setMessage(errorMsg);
      antMessage.error(errorMsg);
    }
    setLoading(false);
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      'confirmed': { color: 'green', text: 'Confirmada' },
      'pending': { color: 'orange', text: 'Pendiente' },
      'cancelled': { color: 'red', text: 'Cancelada' },
      'completed': { color: 'blue', text: 'Completada' },
    };
    
    const config = statusConfig[status?.toLowerCase()] || { color: 'default', text: status };
    
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: 'ID Reserva',
      dataIndex: 'reservationId',
      key: 'reservationId',
      sorter: (a, b) => a.reservationId - b.reservationId,
      render: (text) => <Text copyable>{text}</Text>
    },
    {
      title: 'Localizador',
      dataIndex: 'externalId',
      key: 'externalId',
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
      filters: [
        { text: 'Confirmada', value: 'confirmed' },
        { text: 'Pendiente', value: 'pending' },
        { text: 'Cancelada', value: 'cancelled' },
        { text: 'Completada', value: 'completed' },
      ],
      onFilter: (value, record) => record.status?.toLowerCase() === value,
    },
    {
      title: 'Nombre del Huésped',
      dataIndex: 'guestName',
      key: 'guestName',
      sorter: (a, b) => a.guestName?.localeCompare(b.guestName),
    },
    {
      title: 'Fecha Check-in',
      dataIndex: 'checkInDate',
      key: 'checkInDate',
      render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-',
      sorter: (a, b) => dayjs(a.checkInDate).unix() - dayjs(b.checkInDate).unix(),
    },
    {
      title: 'Fecha Check-out',
      dataIndex: 'checkOutDate',
      key: 'checkOutDate',
      render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-',
      sorter: (a, b) => dayjs(a.checkOutDate).unix() - dayjs(b.checkOutDate).unix(),
    },
  ];

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setReservations([]); // Limpiar resultados al cambiar fechas
    setMessage('');
  };

  const handleQuickSelect = (days) => {
    const end = dayjs();
    const start = dayjs().subtract(days, 'day');
    setDateRange([start, end]);
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <Card 
        title={
          <Space>
            <SyncOutlined spin={loading} />
            <Title level={3} style={{ margin: 0 }}>Integración Synergy</Title>
          </Space>
        }
        extra={
          <Space>
            <Tooltip title="Probar conexión con Synergy">
              <Button 
                icon={<ReloadOutlined />}
                onClick={testConnection}
                loading={loading}
              >
                Test Connection
              </Button>
            </Tooltip>
          </Space>
        }
        style={{ 
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          borderRadius: '8px',
          marginBottom: '16px'
        }}
      >
        {/* Selector de fechas */}
        <Card 
          type="inner" 
          title={
            <Space>
              <CalendarOutlined />
              <Text strong>Seleccionar período de reservas</Text>
            </Space>
          }
          style={{ marginBottom: '20px' }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={12} lg={16}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text type="secondary">Rango de fechas:</Text>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current > dayjs().endOf('day')}
                  placeholder={['Fecha inicial', 'Fecha final']}
                  allowClear={false}
                />
              </Space>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Space wrap>
                <Button size="small" onClick={() => handleQuickSelect(7)}>
                  Últimos 7 días
                </Button>
                <Button size="small" onClick={() => handleQuickSelect(15)}>
                  Últimos 15 días
                </Button>
                <Button size="small" onClick={() => handleQuickSelect(30)}>
                  Últimos 30 días
                </Button>
              </Space>
            </Col>
            <Col xs={24}>
              <Button 
                type="primary"
                icon={<DownloadOutlined />}
                onClick={fetchReservations}
                loading={loading}
                size="large"
                block
              >
                Descargar reservas
              </Button>
            </Col>
          </Row>
        </Card>

        {message && (
          <Alert
            message={message}
            type={message.includes('Error') ? 'error' : 'success'}
            showIcon
            icon={message.includes('Error') ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
            closable
            onClose={() => setMessage('')}
            style={{ marginBottom: '20px' }}
          />
        )}

        {loading && !reservations.length ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary">Cargando reservas...</Text>
            </div>
          </div>
        ) : reservations.length > 0 ? (
          <>
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="Total reservas"
                    value={totalReservations}
                    suffix={`en el período`}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="Período seleccionado"
                    value={`${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="Días de búsqueda"
                    value={dateRange[1].diff(dateRange[0], 'day') + 1}
                    suffix="días"
                  />
                </Card>
              </Col>
            </Row>
            
            <Table
              columns={columns}
              dataSource={reservations}
              rowKey="reservationId"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} reservas`,
              }}
              scroll={{ x: true }}
              bordered
              size="middle"
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={columns.length}>
                      <Text strong>Total de reservas: {reservations.length}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">
              No hay reservas para mostrar. Selecciona un rango de fechas y haz clic en "Descargar reservas" para comenzar.
            </Text>
          </div>
        )}
      </Card>

      <Card style={{ marginTop: '16px' }} size="small">
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size="small">
              <Text type="secondary">
                <strong>Período por defecto:</strong> Últimos 7 días
              </Text>
              <Text type="secondary">
                <strong>Última actualización:</strong> {new Date().toLocaleString()}
              </Text>
            </Space>
          </Col>
          <Col>
            <Tag icon={<SyncOutlined />} color="processing">
              {reservations.length} reservas encontradas
            </Tag>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default SynergyIntegration;